import { Lookup } from '../lookup.interface';
import { TooltipContent } from '../tooltip.interface';

export enum DeraQuestionType {
  CheckboxList = 'checkboxList',
  Radio = 'radio',
  Scale = 'scale',
  Comment = 'comment',
  /** Placeholder rendered by a matching `cmDeraCustom` template in the UI; stores no DERA answer. */
  Custom = 'custom',
}

/** Config for a comment box attached to a question. */
export interface DeraCommentConfig {
  /** Label/title shown above the comment box. Defaults to "Comment:". */
  label?: string;
  /** Placeholder/instruction text shown in the comment box. */
  placeholder?: string;
  /** When true, the comment must be filled before the DERA can be completed. */
  required?: boolean;
  /** Error text shown when a required comment is left blank. */
  requiredMessage?: string;
  /** Makes the comment required only when its parent question holds a given value. */
  requiredWhen?: {
    /** Required only when the parent question's value includes/equals this option value. */
    includesOption: string;
  };
}

export interface DeraQuestionLookup extends Lookup {
  type: DeraQuestionType;
  /** Selectable options for checkbox/radio questions, keyed by stored value. */
  options?: { [value: string]: Lookup };
  /** Rendered only when the related information applies ("If applied"). */
  optional?: boolean;
  /** When true, an answer must be provided before the DERA can be completed. */
  required?: boolean;
  /** Makes this question required only when a sibling question holds a given value. */
  requiredWhen?: {
    /** Sibling question key within the same subsection. */
    question: string;
    /** Required only when the sibling's value includes/equals this option value. */
    includesOption: string;
  };
  /** Error text shown when a required question is left blank. */
  requiredMessage?: string;
  /** Placeholder/instruction text for free-text (comment) questions. */
  placeholder?: string;
  /** Attach a comment box to this question. Pass `true`/`false` for a plain optional comment, or a config object. */
  comment?: boolean | DeraCommentConfig;
  /** Attaches an info ("i") icon next to the label that opens a popup with this content. `title` defaults to the question's display value. */
  tooltip?: TooltipContent;
}

export interface DeraSubsectionLookup extends Lookup {
  optional?: boolean;
  questions: { [key: string]: DeraQuestionLookup };
}

/** Convenience options for a simple Yes/No radio question. */
export const DeraYesNoOptions: { [value: string]: Lookup } = {
  yes: { displayValue: 'Yes' },
  no: { displayValue: 'No' },
};

/** Standard 1-5 rating scale with an "unknown" (?) option, used by DERA scale questions. */
export const DeraOneToFiveScaleOptions: { [value: string]: Lookup } = {
  '1': { displayValue: '1' },
  '2': { displayValue: '2' },
  '3': { displayValue: '3' },
  '4': { displayValue: '4' },
  '5': { displayValue: '5' },
  unknown: { displayValue: '?' },
};

/** Comment config that becomes mandatory when its parent question holds `includesOption`. */
export function deraRequiredComment(includesOption: string, placeholder: string): DeraCommentConfig {
  return {
    requiredWhen: { includesOption },
    requiredMessage: 'Please leave a comment',
    placeholder,
  };
}
